import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Building2, Plus, ImageUp, BarChart3, Printer, Download, Loader2, ArrowLeft, ArrowRight, Check, Trash2, UserPlus, Pencil } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STEPS = ["Branding", "Access", "First Users"];

function CreateDealership({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", contact_email: "", allowed_domain: "", state: "", dms: "", oem: "GM" });
  const [logoFile, setLogoFile] = useState(null);
  const [users, setUsers] = useState([{ name: "", email: "", password: "" }]);
  const logoRef = useRef(null);

  const reset = () => {
    setStep(0); setF({ name: "", contact_email: "", allowed_domain: "", state: "", dms: "", oem: "GM" });
    setLogoFile(null); setUsers([{ name: "", email: "", password: "" }]);
  };
  const close = () => { setOpen(false); reset(); };

  const next = () => {
    if (step === 0 && (!f.name.trim() || !f.contact_email.trim()))
      return toast.error("Dealership name and contact email are required");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const setUser = (i, k, v) => setUsers((arr) => arr.map((u, j) => (j === i ? { ...u, [k]: v } : u)));
  const addUserRow = () => setUsers((arr) => [...arr, { name: "", email: "", password: "" }]);
  const removeUserRow = (i) => setUsers((arr) => arr.filter((_, j) => j !== i));

  const finish = async () => {
    if (!f.name.trim() || !f.contact_email.trim()) { setStep(0); return toast.error("Name and contact email are required"); }
    const validUsers = users.filter((u) => u.name.trim() && u.email.trim());
    for (const u of validUsers) {
      if (u.password.length < 6) return toast.error(`Set a 6+ char password for ${u.email}`);
    }
    setBusy(true);
    try {
      const { data: dealer } = await api.post("/dealerships", f);
      if (logoFile) {
        const fd = new FormData(); fd.append("file", logoFile);
        await api.post(`/dealerships/${dealer.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => toast.warning("Logo upload failed — you can add it later"));
      }
      let userErr = 0;
      for (const u of validUsers) {
        try {
          await api.post("/users", { ...u, role: "user", dealership_id: dealer.id });
        } catch { userErr += 1; }
      }
      toast.success(`Dealership "${f.name}" set up${validUsers.length ? ` with ${validUsers.length - userErr} user(s)` : ""}${userErr ? ` · ${userErr} user(s) failed` : ""}`);
      close(); onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create dealership");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild><Button data-testid="add-dealership-button"><Plus className="h-4 w-4" /> Add Dealership</Button></DialogTrigger>
      <DialogContent className="max-w-lg bg-card" data-testid="add-dealership-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">Onboard a Dealership</DialogTitle>
          <DialogDescription>Guided setup — branding, access, and first users in one flow.</DialogDescription>
        </DialogHeader>

        <div className="mb-1 flex items-center gap-2" data-testid="onboard-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full font-mono text-[11px] ${i <= step ? "bg-violet-600 text-white" : "bg-secondary text-muted-foreground"}`}>{i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
              <span className={`text-xs font-semibold ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              {i < STEPS.length - 1 && <span className="mx-1 h-px w-5 bg-border" />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="onboard-step-branding">
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Dealership Name</Label>
              <Input data-testid="dealership-name" className="bg-secondary/40" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Contact Email (monthly report)</Label>
              <Input type="email" data-testid="dealership-email" className="bg-secondary/40" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Logo (optional)</Label>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="dealership-logo-input" />
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} data-testid="dealership-logo-pick"><ImageUp className="h-3.5 w-3.5" /> {logoFile ? "Change" : "Upload"} logo</Button>
                {logoFile && <span className="truncate font-mono text-xs text-muted-foreground">{logoFile.name}</span>}
              </div></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="onboard-step-access">
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Allowed Email Domain (optional)</Label>
              <div className="flex items-center rounded-md border border-border bg-secondary/40 pl-3">
                <span className="font-mono text-sm text-muted-foreground">@</span>
                <Input data-testid="dealership-domain" className="border-0 bg-transparent focus-visible:ring-0" placeholder="prestige.com" value={f.allowed_domain} onChange={(e) => setF({ ...f, allowed_domain: e.target.value })} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">If set, only emails on this domain can be added to this dealership or reset their password. Leave blank to allow any email.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Dealer State</Label>
                <Input data-testid="dealership-state" className="bg-secondary/40" placeholder="Illinois" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></div>
              <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">DMS</Label>
                <Input data-testid="dealership-dms" className="bg-secondary/40" placeholder="Dealertrack" value={f.dms} onChange={(e) => setF({ ...f, dms: e.target.value })} /></div>
              <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">OEM</Label>
                <Input data-testid="dealership-oem" className="bg-secondary/40" placeholder="GM" value={f.oem} onChange={(e) => setF({ ...f, oem: e.target.value })} /></div>
            </div>
            <p className="text-xs text-muted-foreground">State, DMS &amp; OEM drive brand-specific checks (e.g. Illinois labor-time, Dealertrack "ADDED OPERATION", GM labor-op rule packs).</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="onboard-step-users">
            <p className="text-xs text-muted-foreground">Add the dealership's first warranty administrators. They sign in with the email &amp; temporary password you set. You can skip and add users later.</p>
            {users.map((u, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`onboard-user-row-${i}`}>
                <Input placeholder="Name" className="bg-secondary/40" value={u.name} onChange={(e) => setUser(i, "name", e.target.value)} data-testid={`onboard-user-name-${i}`} />
                <Input type="email" placeholder="Email" className="bg-secondary/40" value={u.email} onChange={(e) => setUser(i, "email", e.target.value)} data-testid={`onboard-user-email-${i}`} />
                <Input type="text" placeholder="Temp password" className="bg-secondary/40" value={u.password} onChange={(e) => setUser(i, "password", e.target.value)} data-testid={`onboard-user-pass-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="text-red-400" onClick={() => removeUserRow(i)} disabled={users.length === 1} data-testid={`onboard-user-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addUserRow} data-testid="onboard-add-user"><UserPlus className="h-3.5 w-3.5" /> Add another user</Button>
          </div>
        )}

        <DialogFooter className="mt-2">
          {step > 0 ? <Button variant="ghost" onClick={back} data-testid="onboard-back"><ArrowLeft className="h-4 w-4" /> Back</Button> : <Button variant="ghost" onClick={close}>Cancel</Button>}
          {step < STEPS.length - 1
            ? <Button onClick={next} data-testid="onboard-next">Next <ArrowRight className="h-4 w-4" /></Button>
            : <Button onClick={finish} disabled={busy} data-testid="onboard-finish">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Finish Setup</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDealership({ dealership, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({
      name: dealership.name || "", contact_email: dealership.contact_email || "",
      allowed_domain: dealership.allowed_domain || "", state: dealership.state || "",
      dms: dealership.dms || "", oem: dealership.oem || "GM",
    });
  }, [open, dealership]);

  const save = async () => {
    if (!f.name?.trim() || !f.contact_email?.trim()) return toast.error("Name and contact email are required");
    setBusy(true);
    try {
      await api.patch(`/dealerships/${dealership.id}`, f);
      toast.success("Dealership updated");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`edit-dealership-${dealership.name}`} title="Edit dealership"><Pencil className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="max-w-md bg-card" data-testid="edit-dealership-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">Edit {dealership.name}</DialogTitle>
          <DialogDescription>Update branding, access domain and the OEM/State/DMS that drive compliance checks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Dealership Name</Label>
            <Input data-testid="edit-name" className="bg-secondary/40" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Contact Email</Label>
            <Input type="email" data-testid="edit-email" className="bg-secondary/40" value={f.contact_email || ""} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
          <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">Allowed Email Domain</Label>
            <div className="flex items-center rounded-md border border-border bg-secondary/40 pl-3">
              <span className="font-mono text-sm text-muted-foreground">@</span>
              <Input data-testid="edit-domain" className="border-0 bg-transparent focus-visible:ring-0" placeholder="any" value={f.allowed_domain || ""} onChange={(e) => setF({ ...f, allowed_domain: e.target.value })} />
            </div></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">State</Label>
              <Input data-testid="edit-state" className="bg-secondary/40" value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value })} /></div>
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">DMS</Label>
              <Input data-testid="edit-dms" className="bg-secondary/40" value={f.dms || ""} onChange={(e) => setF({ ...f, dms: e.target.value })} /></div>
            <div><Label className="mb-1.5 block font-mono text-[11px] uppercase text-muted-foreground">OEM</Label>
              <Input data-testid="edit-oem" className="bg-secondary/40" value={f.oem || ""} onChange={(e) => setF({ ...f, oem: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ dealership }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState("");
  const [rep, setRep] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/dealerships/${dealership.id}/report${month ? `?month=${month}` : ""}`);
      setRep(data);
    } catch { toast.error("Failed to load report"); }
  }, [dealership.id, month]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const printIt = () => {
    if (!rep) return;
    const w = window.open("", "_blank", "width=800,height=700"); if (!w) return;
    const rows = [["Total audits", rep.total_audits], ["Compliant", rep.compliant], ["Compliance rate %", rep.compliance_rate],
      ["Flagged", rep.flagged], ["AI scans", rep.ai_scans], ["Est. AI cost (USD)", rep.est_ai_cost_usd],
      ["Users", rep.users], ["Active users", rep.active_users]]
      .map(([k, v]) => `<tr><td style="padding:6px 12px;border:1px solid #ccc">${k}</td><td style="padding:6px 12px;border:1px solid #ccc"><b>${v}</b></td></tr>`).join("");
    w.document.write(`<html><head><title>Usage Report</title></head><body style="font-family:Arial;padding:28px">
      <h1>Warranty Compliance — Monthly Usage Report</h1><h2>${rep.dealership} · ${rep.month}</h2>
      <table style="border-collapse:collapse;font-size:14px">${rows}</table></body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`report-btn-${dealership.name}`}><BarChart3 className="h-3.5 w-3.5" /> Report</Button></DialogTrigger>
      <DialogContent className="bg-card" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">{dealership.name} — Usage</DialogTitle>
          <DialogDescription>Monthly usage report. Pick a month, then print or download CSV.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-2">
          <Input type="month" className="w-44 bg-secondary/40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" />
          <span className="font-mono text-xs text-muted-foreground">{rep?.month}</span>
        </div>
        {rep && (
          <div className="mt-2 grid grid-cols-2 gap-3">
            {[["Total Audits", rep.total_audits], ["Compliance %", `${rep.compliance_rate}%`], ["Flagged", rep.flagged],
              ["AI Scans", rep.ai_scans], ["Est. AI Cost", `$${rep.est_ai_cost_usd}`], ["Active Users", `${rep.active_users}/${rep.users}`]]
              .map(([l, v]) => (
                <div key={l} className="rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                  <p className="font-heading text-2xl font-extrabold">{v}</p>
                </div>
              ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => window.open(`${BACKEND}/api/dealerships/${dealership.id}/report?fmt=csv${month ? `&month=${month}` : ""}`, "_blank")} data-testid="report-csv"><Download className="h-4 w-4" /> CSV</Button>
          <Button onClick={printIt} data-testid="report-print"><Printer className="h-4 w-4" /> Print</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DealershipsPanel() {
  const [rows, setRows] = useState([]);
  const fileRefs = useRef({});
  const load = useCallback(async () => {
    try { setRows((await api.get("/dealerships")).data); } catch { toast.error("Failed to load dealerships"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadLogo = async (id, e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/dealerships/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo updated"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Logo upload failed"); }
  };

  const removeLogo = async (id) => {
    try { await api.delete(`/dealerships/${id}/logo`); toast.success("Logo removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove logo"); }
  };

  return (
    <div className="rounded-xl border border-border bg-card/90 shadow-lg">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase tracking-tight"><Building2 className="h-4 w-4 text-violet-400" /> Dealerships</h2>
        <CreateDealership onCreated={load} />
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="dealerships-table">
          <TableHeader><TableRow className="border-border hover:bg-transparent">
            <TableHead className="font-mono text-[11px] uppercase">Dealership</TableHead>
            <TableHead className="font-mono text-[11px] uppercase">Domain</TableHead>
            <TableHead className="font-mono text-[11px] uppercase">Users</TableHead>
            <TableHead className="font-mono text-[11px] uppercase">Audits</TableHead>
            <TableHead className="font-mono text-[11px] uppercase">Logo</TableHead>
            <TableHead className="text-right font-mono text-[11px] uppercase">Report</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id} className="border-border" data-testid={`dealership-row-${d.name}`}>
                <TableCell><span className="font-semibold">{d.name}</span><span className="block font-mono text-[10px] text-muted-foreground">{d.contact_email}</span></TableCell>
                <TableCell>{d.allowed_domain ? <Badge variant="outline" className="font-mono text-[10px] text-violet-300 border-violet-500/40 bg-violet-500/15">@{d.allowed_domain}</Badge> : <span className="font-mono text-[11px] text-muted-foreground">any</span>}</TableCell>
                <TableCell className="font-mono">{d.user_count}</TableCell>
                <TableCell className="font-mono">{d.ro_count}</TableCell>
                <TableCell>
                  <input type="file" accept="image/*" className="hidden" ref={(el) => (fileRefs.current[d.id] = el)} onChange={(e) => uploadLogo(d.id, e)} />
                  <div className="flex items-center gap-2">
                    {d.has_logo && <img src={`${BACKEND}/api/dealerships/${d.id}/logo`} alt="logo" className="h-6 w-6 rounded bg-white object-contain" />}
                    <Button variant="ghost" size="sm" onClick={() => fileRefs.current[d.id]?.click()} data-testid={`logo-upload-${d.name}`}><ImageUp className="h-3.5 w-3.5" /> {d.has_logo ? "Change" : "Upload"}</Button>
                    {d.has_logo && <Button variant="ghost" size="icon" className="text-red-400" onClick={() => removeLogo(d.id)} data-testid={`logo-remove-${d.name}`} title="Remove logo"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </TableCell>
                <TableCell className="text-right"><div className="flex items-center justify-end gap-1"><EditDealership dealership={d} onSaved={load} /><ReportDialog dealership={d} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
